import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, User, Phone, Globe, Smartphone, Sun, Moon, Network, Palette } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePreview } from "@/context/PreviewContext";
import { FadeIn, StatusPill } from "@/components/common";
import {
  ProducerShell, ProducerNav, PSectionLabel, PSpinner, useProducerTheme,
} from "@/components/producer";
import { FlightControlPanel } from "@/pages/FlightControl";

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 p-inset px-4 py-3">
      <span className="flex items-center gap-2.5 text-sm p-muted">
        <Icon className="h-4 w-4 p-subtle" /> {label}
      </span>
      <span className="text-right text-sm font-semibold">{value || <span className="p-subtle font-normal">Not set</span>}</span>
    </div>
  );
}

const SECTIONS = [
  { key: "organization", label: "Organization", icon: Network },
  { key: "appearance", label: "Appearance", icon: Palette },
  { key: "flight-control", label: "Flight Control", icon: Smartphone },
];

function Organization({ prod, user, navigate }) {
  if (!prod) {
    return (
      <div className="p-card p-8 text-center">
        <h3 className="font-display text-xl font-semibold">No producer profile yet</h3>
        <p className="mt-2 text-sm p-muted">Complete onboarding to set up your business profile.</p>
        <button onClick={() => navigate("/producer")} className="p-btn mt-5 px-5 py-2.5 text-sm">Go to Events</button>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="p-card p-6" data-testid="account-business">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Business profile</h3>
          {prod.status && <StatusPill status={prod.status} testId="account-status" />}
        </div>
        <div className="mt-4 space-y-2.5">
          <Row icon={Building2} label="Business" value={prod.business_name} />
          <Row icon={User} label="Contact" value={prod.contact_name || prod.name} />
          <Row icon={Phone} label="Phone" value={prod.phone} />
          <Row icon={Globe} label="Website" value={prod.website} />
          <Row icon={User} label="Sign-in email" value={prod.email || user?.email} />
        </div>
      </div>
      <div className="p-card p-6" data-testid="account-plan">
        <h3 className="font-display text-lg font-semibold">Plan & fee</h3>
        <div className="mt-4 p-inset p-5">
          <p className="font-mono-g text-4xl font-black p-accent-text">$10</p>
          <p className="mt-1 text-sm p-muted">per surveyed guest — the informational Goose fee.</p>
          <p className="mt-3 text-xs p-subtle">{prod.terms_version || "v1 — $10 per surveyed guest"}</p>
        </div>
        <p className="mt-3 text-xs p-subtle">Billing is informational in V1 — no card is charged.</p>
      </div>
    </div>
  );
}

function Appearance() {
  const { theme, toggle } = useProducerTheme();
  return (
    <div className="p-card max-w-lg p-6" data-testid="account-appearance">
      <h3 className="font-display text-lg font-semibold">Appearance</h3>
      <p className="mt-1 text-sm p-muted">Choose how the Goose producer app looks on this device.</p>
      <div className="mt-4 flex gap-3">
        <button data-testid="account-theme-light" onClick={() => theme !== "light" && toggle()}
          className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${theme === "light" ? "p-accent-text" : "p-border-c p-muted"}`}
          style={theme === "light" ? { borderColor: "var(--p-accent)", background: "var(--p-accent-soft)" } : undefined}>
          <Sun className="h-4 w-4" /> Light
        </button>
        <button data-testid="account-theme-dark" onClick={() => theme !== "dark" && toggle()}
          className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${theme === "dark" ? "p-accent-text" : "p-border-c p-muted"}`}
          style={theme === "dark" ? { borderColor: "var(--p-accent)", background: "var(--p-accent-soft)" } : undefined}>
          <Moon className="h-4 w-4" /> Dark
        </button>
      </div>
    </div>
  );
}

export default function ProducerAccount() {
  const { user } = useAuth();
  const { preview } = usePreview();
  const navigate = useNavigate();
  const [section, setSection] = useState("organization");

  const { data, isLoading } = useQuery({
    queryKey: ["producer-status", preview?.producer_id],
    queryFn: async () => (await api.get("/producer/status")).data,
  });

  if (isLoading) return <ProducerShell><ProducerNav active="account" /><PSpinner /></ProducerShell>;
  const prod = data?.producer;

  return (
    <ProducerShell>
      <ProducerNav active="account" />
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_1fr]">
          {/* sub-nav */}
          <FadeIn>
            <div>
              <PSectionLabel>Account</PSectionLabel>
              <nav className="mt-4 space-y-1">
                {SECTIONS.map((s) => (
                  <button key={s.key} data-testid={`account-nav-${s.key}`} onClick={() => setSection(s.key)}
                    className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${section === s.key ? "p-accent-text" : "p-muted hover:opacity-80"}`}
                    style={section === s.key ? { background: "var(--p-accent-soft)" } : undefined}>
                    <s.icon className="h-4 w-4" /> {s.label}
                  </button>
                ))}
              </nav>
            </div>
          </FadeIn>

          {/* content */}
          <FadeIn delay={0.05} className="min-w-0">
            {section === "organization" && <Organization prod={prod} user={user} navigate={navigate} />}
            {section === "appearance" && <Appearance />}
            {section === "flight-control" && <FlightControlPanel />}
          </FadeIn>
        </div>
      </div>
    </ProducerShell>
  );
}
